import { HighlightOffOutlined } from '@mui/icons-material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { Box, Chip, Grid, LinearProgress, Tooltip, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import agent from '../../../api/agent'
import { IProfileProgress } from '../../../interfaces/persons/IPerson'
import { useStore } from '../../../Provider'
import '../../styles/grid.scss'
import '../profile.scss'

const PersonProfileProgress = (): JSX.Element => {
  const { t } = useTranslation()

  const rootStore = useStore()
  const { validatedUser } = rootStore.loginStore

  // Local progress state and fetch
  const [profileProgress, setProfileProgress] = useState<IProfileProgress | null>(null)
  const [loadingProfileProgress, setLoadingProfileProgress] = useState<boolean>(false)

  useEffect(() => {
    let isActive = true
    const fetchProgress = async () => {
      setLoadingProfileProgress(true)
      try {
        const data = await agent.Persons.getProfileProgress(validatedUser)
        if (isActive) setProfileProgress(data as IProfileProgress)
      } catch {
        // ignore
      } finally {
        if (isActive) setLoadingProfileProgress(false)
      }
    }
    fetchProgress()
    return () => {
      isActive = false
    }
  }, [validatedUser])

  const clampPercent = (n?: number) => {
    const v = typeof n === 'number' && !isNaN(n) ? n : 0
    return Math.max(0, Math.min(100, v))
  }

  const nz = (n?: number) => (typeof n === 'number' && !isNaN(n) ? n : 0)

  const normalizePercent = (score?: number, weight?: number) => {
    const s = typeof score === 'number' && !isNaN(score) ? score : 0
    const w = typeof weight === 'number' && weight > 0 ? weight : 0
    return w === 0 ? 0 : clampPercent((s / w) * 100)
  }

  const buildBreakdown = (r: IProfileProgress) => {
    // Static weights are only for displaying the max contribution, not for calculations.
    const W = {
      title: 5,
      summary: 10,
      workExp: 10,
      languages: 5,
      employers: 5,
      educations: 5,
      projSkills: 15,
      projRoles: 15,
      projDesc: 10,
      skillExp: 20,
    }

    return [
      { key: 'title', label: t('ProfileProgress.Title'), weight: W.title, percent: normalizePercent(r.titleScore, W.title) },
      { key: 'summary', label: t('ProfileProgress.Summary'), weight: W.summary, percent: normalizePercent(r.summaryScore, W.summary) },
      {
        key: 'workExp',
        label: t('ProfileProgress.WorkExp'),
        weight: W.workExp,
        percent: normalizePercent(r.workExpDescriptionScore, W.workExp),
      },
      {
        key: 'languages',
        label: t('ProfileProgress.Languages'),
        weight: W.languages,
        percent: normalizePercent(r.languagesScore, W.languages),
      },
      {
        key: 'employers',
        label: t('ProfileProgress.Employers'),
        weight: W.employers,
        percent: normalizePercent(r.employerScore, W.employers),
      },
      {
        key: 'educations',
        label: t('ProfileProgress.Educations'),
        weight: W.educations,
        percent: normalizePercent(r.educationScore, W.educations),
      },
      {
        key: 'projSkills',
        label: t('ProfileProgress.ProjectSkills'),
        weight: W.projSkills,
        percent: normalizePercent(r.projectsSkillsScore, W.projSkills),
        extra: { n: nz(r.projectsWithSkills), d: nz(r.personProjectCount) },
      },
      {
        key: 'projRoles',
        label: t('ProfileProgress.ProjectRoles'),
        weight: W.projRoles,
        percent: normalizePercent(r.projectsRolesScore, W.projRoles),
        extra: { n: nz(r.projectsWithRoles), d: nz(r.personProjectCount) },
      },
      {
        key: 'projDesc',
        label: t('ProfileProgress.ProjectDescriptions'),
        weight: W.projDesc,
        percent: normalizePercent(r.projectsDescriptionsScore, W.projDesc),
        extra: { n: nz(r.projectsWithDescriptions), d: nz(r.personProjectCount) },
      },
      {
        key: 'skillExp',
        label: t('ProfileProgress.SkillExperience'),
        weight: W.skillExp,
        percent: normalizePercent(r.skillsExperienceScore, W.skillExp),
        extra: { n: nz(r.skillsWithExperienceTimes), d: nz(r.totalSkills) },
      },
    ]
  }

  type RowInfo = ReturnType<typeof buildBreakdown>[number]

  const tooltipTextFor = (row: RowInfo) => {
    const tt = (key: string, vars?: Record<string, unknown>) => t(key, { ...(vars || {}) }) as string
    const varsND = (r: RowInfo) => ({
      n: r.extra?.n ?? 0,
      d: r.extra?.d ?? 0,
    })
    switch (row.key) {
      case 'title':
        return tt('ProfileProgress.Tooltips.Title')
      case 'summary':
        return tt('ProfileProgress.Tooltips.Summary')
      case 'workExp':
        return tt('ProfileProgress.Tooltips.WorkExp')
      case 'languages':
        return tt('ProfileProgress.Tooltips.Languages')
      case 'employers':
        return tt('ProfileProgress.Tooltips.Employers')
      case 'educations':
        return tt('ProfileProgress.Tooltips.Educations')
      case 'projSkills':
        return tt('ProfileProgress.Tooltips.ProjectSkills', varsND(row))
      case 'projRoles':
        return tt('ProfileProgress.Tooltips.ProjectRoles', varsND(row))
      case 'projDesc':
        return tt('ProfileProgress.Tooltips.ProjectDescriptions', varsND(row))
      case 'skillExp':
        return tt('ProfileProgress.Tooltips.SkillExperience', varsND(row))
      default:
        return tt('ProfileProgress.Tooltips.Default')
    }
  }

  // A row is complete when its rounded percentage matches its weight.
  const isRowComplete = (row: { percent: number; weight: number }) => Math.round(row.percent) === 100

  // Map progress keys to the correct edit routes
  const routeFor = (key: string): string => {
    switch (key) {
      case 'title':
      case 'summary':
      case 'workExp':
      case 'languages':
        return '/profile/overview'
      case 'educations':
        return '/profile/certedu'
      case 'employers':
      case 'projSkills':
      case 'projRoles':
      case 'projDesc':
      case 'skillExp':
        return '/profile/workhistory'
      default:
        return '/profile/overview'
    }
  }

  // Build a "next action" label + color for each row
  const nextActionFor = (row: RowInfo): { label: string; color: 'success' | 'warning' | 'error' } => {
    const pct = Math.round(row.percent)
    const complete = isRowComplete(row)
    const zero = pct === 0
    const d = row.extra?.d ?? 0
    const n = row.extra?.n ?? 0
    const tt = (k: string, def: string) => t(k, { defaultValue: def }) as string

    if (complete) return { label: tt('ProfileProgress.Actions.Complete', 'Complete'), color: 'success' }

    switch (row.key) {
      case 'title':
        return { label: tt('ProfileProgress.Actions.AddTitle', 'Add title'), color: zero ? 'error' : 'warning' }
      case 'summary':
        return { label: tt('ProfileProgress.Actions.WriteSummary', 'Write summary'), color: zero ? 'error' : 'warning' }
      case 'workExp':
        return { label: tt('ProfileProgress.Actions.DescribeWork', 'Describe experience'), color: zero ? 'error' : 'warning' }
      case 'languages':
        return { label: tt('ProfileProgress.Actions.AddLanguages', 'Add languages'), color: zero ? 'error' : 'warning' }
      case 'employers':
        return { label: tt('ProfileProgress.Actions.AddEmployers', 'Add employers'), color: zero ? 'error' : 'warning' }
      case 'educations':
        return { label: tt('ProfileProgress.Actions.AddEducation', 'Add education'), color: zero ? 'error' : 'warning' }
      case 'projSkills':
        return {
          label:
            d === 0
              ? tt('ProfileProgress.Actions.AddProjects', 'Add projects')
              : tt('ProfileProgress.Actions.AddProjectSkills', 'Add skills to projects'),
          color: zero ? 'error' : 'warning',
        }
      case 'projRoles':
        return {
          label:
            d === 0
              ? tt('ProfileProgress.Actions.AddProjects', 'Add projects')
              : tt('ProfileProgress.Actions.AddProjectRoles', 'Add roles to projects'),
          color: zero ? 'error' : 'warning',
        }
      case 'projDesc':
        return {
          label:
            d === 0
              ? tt('ProfileProgress.Actions.AddProjects', 'Add projects')
              : tt('ProfileProgress.Actions.WriteProjectDescriptions', 'Write project descriptions'),
          color: zero ? 'error' : 'warning',
        }
      case 'skillExp':
        return {
          label:
            d === 0
              ? tt('ProfileProgress.Actions.AddSkills', 'Add skills')
              : tt('ProfileProgress.Actions.AddSkillExperience', 'Add skill experience'),
          color: zero ? 'error' : 'warning',
        }
      default:
        return { label: tt('ProfileProgress.Actions.Continue', 'Continue'), color: zero ? 'error' : 'warning' }
    }
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <p data-testid='title' className='profile--titlefont'>
          {t('ProfileProgress.Header')}
        </p>
      </Grid>
      <Grid item xs={12}>
        {loadingProfileProgress ? (
          <LinearProgress />
        ) : profileProgress ? (
          (() => {
            const totalPercent = clampPercent(profileProgress.totalScore)
            const breakdown = buildBreakdown(profileProgress)
            return (
              <>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item xs={12}>
                    <LinearProgress variant='determinate' value={totalPercent} />
                  </Grid>
                  <Grid item xs={12}>
                    {(() => {
                      const scoreColor = totalPercent >= 80 ? 'success.main' : totalPercent >= 50 ? 'warning.main' : 'error.main'
                      return (
                        <Box
                          display='flex'
                          justifyContent={'flex-end'}
                          alignItems='baseline'
                          gap={1}
                          aria-label={t('ProfileProgress.Score')}
                        >
                          {/*                           <Typography component='span' variant='body2' color='text.secondary'>
                            {t('ProfileProgress.Score')}
                          </Typography> */}
                          <Typography component='span' variant='h5' fontWeight={700} sx={{ color: scoreColor }}>
                            {Math.round(totalPercent)}%
                          </Typography>
                        </Box>
                      )
                    })()}
                  </Grid>
                </Grid>
                <Grid container spacing={0.8} mt={1}>
                  {breakdown.map((row) => (
                    <Grid item xs={12} key={row.key}>
                      <Grid container justifyContent='space-between' alignItems='center'>
                        <Grid item>
                          <Tooltip title={tooltipTextFor(row)} arrow enterDelay={300} placement='bottom-start'>
                            <Link to={routeFor(row.key)} style={{ textDecoration: 'none', color: 'inherit' }}>
                              {isRowComplete(row) ? (
                                <CheckCircleOutlineIcon fontSize='small' color='success' sx={{ verticalAlign: 'middle', mr: 1 }} />
                              ) : (
                                <HighlightOffOutlined fontSize='small' color='error' sx={{ verticalAlign: 'middle', mr: 1 }} />
                              )}
                              <span className='profile--deptext'>
                                {row.label}
                                {row.extra && (
                                  <>
                                    {' '}
                                    <span className='profile--titlecaption'>
                                      {row.extra.n ?? 0} {t('ProfileProgress.Of')} {row.extra.d ?? 0}
                                    </span>
                                  </>
                                )}
                              </span>
                            </Link>
                          </Tooltip>
                        </Grid>
                        <Grid item>
                          <Link to={routeFor(row.key)} style={{ textDecoration: 'none', color: 'inherit' }}>
                            {(() => {
                              const action = nextActionFor(row)
                              return (
                                <Chip
                                  clickable={action.color !== 'success'}
                                  size='small'
                                  label={action.label}
                                  color={action.color}
                                  variant={action.color === 'success' ? 'filled' : 'outlined'}
                                />
                              )
                            })()}
                          </Link>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </>
            )
          })()
        ) : (
          <span className='profile--deptext'>{t('Profile.NoAddedSummary')}</span>
        )}
      </Grid>
    </Grid>
  )
}

export default observer(PersonProfileProgress)
